import React, { useState, useEffect, useRef } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ChevronDown, ChevronRight, Phone, Mail, MapPin, Search } from "lucide-react";
import { Button } from "./ui/button";
import { navLinks, company } from "../mock";
import Editable from "./Editable";
import SearchDialog from "./SearchDialog";

function DropdownItem({ item }) {
  const [open, setOpen] = useState(false);
  const [openChild, setOpenChild] = useState(null);
  const rootRef = useRef(null);
  const listItemRefs = useRef({});
  const [flyoutTop, setFlyoutTop] = useState(0);
  const closeTimerRef = useRef(null);
  const childCloseTimerRef = useRef(null);
  const { pathname } = useLocation();

  // Close on route change
  useEffect(() => {
    setOpen(false);
    setOpenChild(null);
  }, [pathname]);

  // Escape closes
  useEffect(() => {
    if (!open) return;
    const onKey = (e) => {
      if (e.key === "Escape") { setOpen(false); setOpenChild(null); }
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [open]);

  // Compute flyout vertical offset relative to the current open child button
  useEffect(() => {
    if (!openChild) return;
    const el = listItemRefs.current[openChild];
    if (el) setFlyoutTop(el.offsetTop);
  }, [openChild]);

  // Cleanup timers on unmount
  useEffect(() => () => {
    if (closeTimerRef.current) clearTimeout(closeTimerRef.current);
    if (childCloseTimerRef.current) clearTimeout(childCloseTimerRef.current);
  }, []);

  const handleRootEnter = () => {
    if (closeTimerRef.current) { clearTimeout(closeTimerRef.current); closeTimerRef.current = null; }
    setOpen(true);
  };
  const handleRootLeave = () => {
    closeTimerRef.current = setTimeout(() => {
      setOpen(false);
      setOpenChild(null);
    }, 180);
  };
  const handleChildEnter = (label) => {
    if (childCloseTimerRef.current) { clearTimeout(childCloseTimerRef.current); childCloseTimerRef.current = null; }
    setOpenChild(label);
  };
  const handleChildLeave = () => {
    childCloseTimerRef.current = setTimeout(() => setOpenChild(null), 220);
  };

  const openChildData = item.children.find((c) => c.submenu && c.label === openChild);

  return (
    <div
      className="relative"
      ref={rootRef}
      onMouseEnter={handleRootEnter}
      onMouseLeave={handleRootLeave}
    >
      {/* Trigger: label routes to page, chevron indicates submenu */}
      <div className="flex items-center gap-1">
        <Link
          to={item.to}
          className="text-sm font-medium text-foreground/85 hover:text-brand link-underline"
          data-testid={`nav-link-${item.label.toLowerCase().replace(/\s+/g, "-")}`}
        >
          {item.label}
        </Link>
        <ChevronDown
          size={14}
          className={`text-foreground/60 transition-transform duration-200 ${open ? "rotate-180" : ""}`}
          aria-hidden="true"
        />
      </div>

      {/* Flyout wrapper: always mounted, animated via classes for smooth UX */}
      <div
        className={`absolute top-full left-1/2 -translate-x-1/2 pt-3 z-50 transition-all duration-200 ${
          open ? "opacity-100 translate-y-0 pointer-events-auto" : "opacity-0 -translate-y-1 pointer-events-none"
        }`}
        aria-hidden={!open}
      >
        <div className="absolute top-[7px] left-1/2 -translate-x-1/2 w-3 h-3 rotate-45 bg-white border-l border-t border-border" />
        {/* Relative wrapper — width stays fixed to main panel so parent centering never shifts */}
        <div className="relative">
          {/* Main dropdown panel */}
          <div className="bg-white shadow-xl rounded-lg min-w-[260px] border border-border py-1">
            {item.label === "Commodities" && (
              <Link
                to={item.to}
                className="block px-5 py-3 text-sm font-semibold hover:bg-[var(--brand-100)] transition rounded-md mx-1"
                style={{ background: "var(--brand-50)", color: "var(--brand-700)" }}
              >
                All Categories →
              </Link>
            )}
            {item.label === "Home" && (
              <Link
                to={item.to}
                className="block px-5 py-3 text-sm font-semibold hover:bg-[var(--brand-100)] transition rounded-md mx-1"
                style={{ background: "var(--brand-50)", color: "var(--brand-700)" }}
              >
                Go to Home →
              </Link>
            )}
            {item.children.map((c) => (
              <div
                key={c.to || c.label}
                ref={(el) => { if (c.submenu) listItemRefs.current[c.label] = el; }}
                onMouseEnter={c.submenu ? () => handleChildEnter(c.label) : undefined}
                onMouseLeave={c.submenu ? handleChildLeave : undefined}
              >
                {c.submenu ? (
                  <Link
                    to={c.to}
                    className={`w-full flex items-center justify-between px-5 py-3 text-sm text-foreground/80 hover:bg-muted hover:text-brand transition text-left ${openChild === c.label ? "bg-muted text-brand" : ""}`}
                    data-testid={`nav-submenu-${c.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
                    style={openChild === c.label ? { color: "var(--brand-700)" } : {}}
                  >
                    <span>{c.label}</span>
                    <ChevronRight size={14} className="text-muted-foreground" />
                  </Link>
                ) : (
                  <Link
                    to={c.to}
                    className="flex items-center justify-between px-5 py-3 text-sm text-foreground/80 hover:bg-muted hover:text-brand transition"
                  >
                    <span>{c.label}</span>
                  </Link>
                )}
              </div>
            ))}
          </div>

          {/* Second-level flyout — ABSOLUTELY positioned to the right of main panel.
              Does NOT affect main panel width, so parent centering never shifts. */}
          <div
            className={`absolute left-full top-0 pl-1 transition-opacity duration-200 ${
              openChildData ? "opacity-100 pointer-events-auto" : "opacity-0 pointer-events-none"
            }`}
            style={{ transform: `translateY(${flyoutTop}px)` }}
            onMouseEnter={() => {
              if (childCloseTimerRef.current) { clearTimeout(childCloseTimerRef.current); childCloseTimerRef.current = null; }
            }}
            onMouseLeave={handleChildLeave}
          >
            {openChildData && (
              <div
                className="bg-white shadow-xl rounded-lg min-w-[300px] border border-border py-1"
                data-testid={`nav-flyout-${openChildData.label.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              >
                {openChildData.submenu.map((sc) => (
                  <Link
                    key={sc.to}
                    to={sc.to}
                    className="block px-5 py-3 text-sm text-foreground/80 hover:bg-muted hover:text-brand transition"
                  >
                    {sc.label}
                  </Link>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default function Header() {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    setOpen(false);
  }, [location.pathname]);

  return (
    <header
      className={`sticky top-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-white/95 backdrop-blur-md shadow-sm" : "bg-white"
      }`}
    >
      {/* Top utility bar */}
      <div className="hidden lg:block brand-gradient text-white text-xs">
        <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-9">
          <div className="flex items-center gap-6 opacity-95 min-w-0">
            <span className="flex items-center gap-2 truncate"><MapPin size={13} className="flex-shrink-0" /> <Editable field="company.address" as="span" className="truncate">{company.address}</Editable></span>
          </div>
          <div className="flex items-center gap-5 flex-shrink-0">
            <span className="flex items-center gap-2"><Phone size={13} /> <Editable field="company.phone" as="span">{company.phone}</Editable></span>
            {company.landline && (
              <span className="flex items-center gap-2 opacity-90">
                <Phone size={13} /> <Editable field="company.landline" as="span">{company.landline}</Editable>
              </span>
            )}
            <a href={`mailto:${company.email}`} className="flex items-center gap-2 hover:underline">
              <Mail size={13} /> <Editable field="company.email" as="span">{company.email}</Editable>
            </a>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-6 flex items-center justify-between h-24">
        <Link to="/" className="flex items-center" aria-label="Ruken Alazdehar General Trading LLC">
          <img
            src="/logo.png"
            alt="Ruken Alazdehar General Trading LLC"
            className="h-14 md:h-[62px] w-auto object-contain"
          />
        </Link>

        {/* Desktop nav */}
        <nav className="hidden lg:flex items-center gap-8">
          {navLinks.map((item, idx) =>
            item.children ? (
              <DropdownItem key={item.label} item={item} />
            ) : (
              <NavLink
                key={item.to}
                to={item.to}
                end={item.to === "/"}
                className={({ isActive }) =>
                  `text-sm font-medium link-underline ${
                    isActive ? "text-brand" : "text-foreground/85 hover:text-brand"
                  }`
                }
              >
                {item.label}
              </NavLink>
            )
          )}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <button
            onClick={() => setSearchOpen(true)}
            className="flex items-center gap-2 px-3.5 h-10 rounded-md border border-border bg-muted/40 hover:bg-muted hover:border-brand-300 transition text-muted-foreground hover:text-brand text-sm group"
            aria-label="Open search"
          >
            <Search size={15} />
            <span className="hidden xl:inline">Search...</span>
            <kbd className="hidden xl:inline text-[10px] px-1.5 py-0.5 rounded border border-border bg-white font-sans text-muted-foreground">⌘K</kbd>
          </button>
          <Link to="/supplier-center">
            <Button className="bg-brand hover:bg-[var(--brand-800)] text-white rounded-md px-5 h-10 font-medium" style={{ backgroundColor: "var(--brand-700)" }}>
              Supplier Center
            </Button>
          </Link>
        </div>

        {/* Mobile actions */}
        <div className="flex items-center gap-2 lg:hidden">
          <button onClick={() => setSearchOpen(true)} aria-label="Search" className="w-9 h-9 rounded-md flex items-center justify-center text-foreground hover:bg-muted">
            <Search size={20} />
          </button>
          <button onClick={() => setOpen(!open)} className="text-foreground" aria-label="Menu">
            {open ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden border-t border-border bg-white">
          <div className="px-6 py-4 flex flex-col gap-1">
            {navLinks.map((item) =>
              item.children ? (
                <details key={item.label} className="group border-b border-border last:border-0">
                  <summary className="flex items-center justify-between py-3 cursor-pointer text-sm font-medium list-none">
                    <Link to={item.to} className="flex-1" onClick={(e) => e.stopPropagation()}>{item.label}</Link>
                    <ChevronDown size={16} className="group-open:rotate-180 transition ml-2" />
                  </summary>
                  <div className="pl-4 pb-2 flex flex-col">
                    <Link to={item.to} className="py-2 text-sm font-semibold" style={{ color: "var(--brand-700)" }}>
                      {item.label === "Commodities" ? "All Categories →" : "Go to " + item.label + " →"}
                    </Link>
                    {item.children.map((c) => (
                      c.submenu ? (
                        <details key={c.label} className="group/child">
                          <summary className="flex items-center justify-between py-2 cursor-pointer text-sm text-foreground/80 list-none">
                            <Link to={c.to} className="flex-1" onClick={(e) => e.stopPropagation()}>{c.label}</Link>
                            <ChevronRight size={14} className="group-open/child:rotate-90 transition ml-2 text-muted-foreground" />
                          </summary>
                          <div className="pl-4 flex flex-col border-l border-border ml-2">
                            {c.submenu.map((sc) => (
                              <Link key={sc.to} to={sc.to} className="py-2 text-sm text-foreground/70 pl-3">
                                {sc.label}
                              </Link>
                            ))}
                          </div>
                        </details>
                      ) : (
                        <Link key={c.to} to={c.to} className="py-2 text-sm text-foreground/80">
                          {c.label}
                        </Link>
                      )
                    ))}
                  </div>
                </details>
              ) : (
                <Link key={item.to} to={item.to} className="py-3 text-sm font-medium border-b border-border">
                  {item.label}
                </Link>
              )
            )}
            <Link to="/supplier-center" className="mt-3">
              <Button className="w-full text-white" style={{ backgroundColor: "var(--brand-700)" }}>Supplier Center</Button>
            </Link>
          </div>
        </div>
      )}
      <SearchDialog open={searchOpen} onOpenChange={setSearchOpen} />
    </header>
  );
}
